import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { TranslocoService } from '@ngneat/transloco';
import { HdBodygraphPlanetaryActivation } from '../../hd-bodygraph.models';
import {
  HD_BODYGRAPH_CIRCUITS,
  getHdBodygraphCircuitGates,
  getHdBodygraphFormatGates,
} from '../../hd-bodygraph-circuit-metadata';
import { HdGateInfoService } from '../../hd-gate-info.service';
import { HdChartHoverHighlightService } from '../../hd-chart-hover-highlight.service';
import { HdContentLanguageService } from '../../hd-content-language.service';

interface CircuitCount {
  /** Stabiele, taal-onafhankelijke identiteit van de rij voor de stat-freeze. */
  key: string;
  name: string;
  fullName: string;
  count: number;
  selectedCount: number;
  formatGates: number[];
  /** Álle poorten van het circuit — bron voor de bestaande hover-matching. */
  gates: readonly number[];
  /** Enkel de geactiveerde poorten — de set die de stat-freeze bevriest. */
  activeGates: readonly number[];
}

@Component({
  selector: 'hd-circuit-stats',
  standalone: true,
  imports: [],
  templateUrl: './hd-circuit-stats.component.html',
  styleUrl: './hd-circuit-stats.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-hover-active]': 'hoverActive()',
    '[class.is-stat-frozen]': 'frozenStatKey() !== null',
    '[class.is-dim-disabled]': 'dimDisabled()',
    '[class.is-no-dim]': 'noDim()',
  },
})
export class HdCircuitStatsComponent {
  readonly planetaryActivations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly selectedActivations = input<readonly HdBodygraphPlanetaryActivation[]>([]);
  readonly selectedSide = input<'design' | 'personality' | null>(null);
  readonly selectedColor = input<string | null>(null);

  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });

  /**
   * `true` zodra de hover-service een live-hover of een freeze actief heeft.
   * Wordt op host-niveau gebruikt om de rij-dim/match-styling te activeren.
   */
  protected readonly hoverActive = computed(() => {
    return this.hoverService?.enabled() === true && this.hoverService?.isActive() === true;
  });

  /**
   * Set van gates die momenteel "actief" zijn volgens de hover-service
   * (live + frozen). Wordt per rij gebruikt om te bepalen of die rij
   * matched (≥1 gate-overlap) of gedimd is.
   */
  protected readonly hoverActiveGates = computed<ReadonlySet<number>>(() => {
    return this.hoverService?.effectiveGates() ?? new Set<number>();
  });

  /** Stat-rijen zijn enkel klikbaar waar de hover/freeze-service actief is. */
  protected readonly statFreezeAvailable = computed(() => this.hoverService?.enabled() === true);

  protected readonly frozenStatKey = computed(() => this.hoverService?.frozenStatKey() ?? null);

  /**
   * "Dimmen bij hover" staat uit (app-setting): rijen dimmen niet, matchende
   * rijen tonen in de plaats een blauwe rand (zie SCSS `.is-dim-disabled`).
   */
  protected readonly dimDisabled = computed(() =>
    this.hoverService != null && !this.hoverService.dimOnHover(),
  );

  /**
   * De grid-kant dimt niet: "dimmen bij hover" uit, of "enkel één kant dimmen"
   * bij een hover/stat-klik vanuit de grid. Anders dan {@link dimDisabled}
   * zonder blauwe match-randen — de highlight blijft zoals bij gewoon dimmen.
   */
  protected readonly noDim = computed(() =>
    this.hoverService != null && !this.hoverService.dimsGrid(),
  );

  /**
   * Tooltip-labels voor de klik-affordance. Volgt hetzelfde patroon als
   * `getFormatTooltip`: de HD content-taal, niet de globale app-taal.
   */
  protected readonly freezeLabels = computed(() => {
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    return {
      freezeRow: this.transloco.translate<string>('humanDesign.planetStats.freezeRowTooltip', undefined, lang),
      unfreezeRow: this.transloco.translate<string>('humanDesign.planetStats.unfreezeRowTooltip', undefined, lang),
    };
  });

  protected isStatClickable(gates: readonly number[]): boolean {
    return this.statFreezeAvailable() && gates.length > 0;
  }

  protected isStatFrozen(key: string): boolean {
    return this.frozenStatKey() === key;
  }

  protected statTooltip(key: string): string {
    return this.isStatFrozen(key) ? this.freezeLabels().unfreezeRow : this.freezeLabels().freezeRow;
  }

  protected onStatClick(key: string, gates: readonly number[]): void {
    if (!this.isStatClickable(gates)) return;
    this.hoverService?.toggleStatFreeze(key, gates);
  }

  protected isRowMatching(gates: readonly number[]): boolean {
    if (!this.hoverActive()) return false;
    const active = this.hoverActiveGates();
    for (const gate of gates) {
      if (active.has(gate)) return true;
    }
    return false;
  }

  protected readonly circuitCounts = computed<readonly CircuitCount[]>(() => {
    const activations = this.planetaryActivations();
    const selectedActivations = this.selectedActivations();
    const formatGatesList = getHdBodygraphFormatGates();
    // Volg de HD content-language override, niet enkel de globale app-taal:
    // de settings-panel-taalwissel gaat via HdContentLanguageService.setOverride
    // en verandert `activeLang` in TranslocoService niet.
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const circuitNames = this.gateInfoService.circuitNamesIn(lang);
    const familyLabels = this.gateInfoService.circuitFamilyLabelsIn(lang);

    // We groeperen op basis van de hoofdcircuits in HD_BODYGRAPH_CIRCUITS.
    // Tribaal ondernemend en Tribaal Community samengeteld onder de noemer Tribaal Ego.

    const circuitGateMap = new Map<
      string,
      { gates: Set<number>; fullName: string; circuitKeys: string[] }
    >();

    // Initialiseer mapping voor hoofdcircuits
    HD_BODYGRAPH_CIRCUITS.forEach(c => {
      const labels = circuitNames[c.key];
      let targetName = labels?.statShortName ?? labels?.shortName ?? labels?.name ?? c.key;
      let targetFullName = labels?.name ?? c.key;

      if (c.key === 'tribaal-ondernemend' || c.key === 'tribaal-community') {
        targetName = 'Ego'; // Overschrijf shortName voor deze twee
        targetFullName = `${familyLabels.tribaal} Ego`;
      }

      if (!circuitGateMap.has(targetName)) {
        circuitGateMap.set(targetName, {
          gates: new Set<number>(),
          fullName: targetFullName,
          circuitKeys: [],
        });
      }

      // De rij-key komt van de circuit-keys, niet van de weergavenaam: die naam
      // is taalafhankelijk en zou bij een taalwissel de bevroren-markering laten
      // wegvallen terwijl de poorten bevroren blijven.
      circuitGateMap.get(targetName)!.circuitKeys.push(c.key);

      const gates = getHdBodygraphCircuitGates(c.key);
      gates.forEach(g => circuitGateMap.get(targetName)!.gates.add(g));
    });

    const counts: Record<string, {
      key: string;
      count: number;
      selectedCount: number;
      fullName: string;
      formatGates: Set<number>;
      gates: readonly number[];
      activeGates: Set<number>;
    }> = {};
    circuitGateMap.forEach((val, name) => {
      counts[name] = {
        key: `circuit:${[...val.circuitKeys].sort().join('+')}`,
        count: 0,
        selectedCount: 0,
        fullName: val.fullName,
        formatGates: new Set<number>(),
        gates: [...val.gates],
        activeGates: new Set<number>(),
      };
      activations.forEach(a => {
        if (val.gates.has(a.gate)) {
          counts[name].count++;
          counts[name].activeGates.add(a.gate);
          if (formatGatesList.includes(a.gate)) {
            counts[name].formatGates.add(a.gate);
          }
        }
      });
      selectedActivations.forEach(activation => {
        if (val.gates.has(activation.gate)) {
          counts[name].selectedCount++;
        }
      });
    });

    return Object.entries(counts)
      .map(([name, val]) => ({
        key: val.key,
        name,
        fullName: val.fullName,
        count: val.count,
        selectedCount: val.selectedCount,
        formatGates: [...val.formatGates].sort((a, b) => a - b),
        gates: val.gates,
        activeGates: [...val.activeGates].sort((a, b) => a - b),
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  });

  protected getFormatTooltip(gates: number[]): string {
    if (gates.length === 0) return '';
    const gatesText = gates.join(', ');
    const key =
      gates.length > 1
        ? 'humanDesign.planetStats.formatGateTooltipOther'
        : 'humanDesign.planetStats.formatGateTooltipOne';
    return this.transloco.translate(key, { gates: gatesText }, this.contentLang.effectiveLang());
  }
}
